import { disconnectSocket } from './socket';

const USER_STORAGE_KEY = 'user';
const EXPIRY_BUFFER_MS = 30 * 1000; // 30 seconds

/**
 * Parses stored user from localStorage. Returns null if missing or invalid.
 */
function getStoredUser() {
    try {
        const raw = localStorage.getItem(USER_STORAGE_KEY);
        if (!raw) return null;
        const user = JSON.parse(raw);
        return user && user.data && user.data.token ? user : null;
    } catch {
        return null;
    }
}

/**
 * Returns the session token string or null if not present.
 */
export function getToken() {
    const user = getStoredUser();
    return user ? user.data.token : null;
}

/**
 * Decodes base64url to string (JWT payload is base64url-encoded).
 * Does not throw; returns null on failure.
 */
function base64UrlDecode(str) {
    try {
        if (!str || typeof str !== 'string') return null;
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad) base64 += '===='.slice(0, 4 - pad);
        return atob(base64);
    } catch {
        return null;
    }
}

/**
 * Checks if the token looks like a JWT (three base64 parts separated by dots).
 */
function isJwtFormat(token) {
    if (typeof token !== 'string') return false;
    const parts = token.split('.');
    return parts.length === 3;
}

/**
 * Safely decodes JWT payload and returns exp (seconds) or null.
 * Uses base64url-safe decode for JWT compatibility.
 */
function getJwtExp(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const decoded = base64UrlDecode(parts[1]);
        if (!decoded) return null;
        const payload = JSON.parse(decoded);
        return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
        return null;
    }
}

/**
 * Returns true if the current session token is expired (or missing/corrupted in a way that implies no valid session).
 * - No user or no token: returns false (so we do not block the request; backend will 401).
 * - Token present: uses JWT exp if token is JWT-shaped, else user.expiresAt from storage.
 * - Uses a small buffer (30s) so we expire slightly before server would.
 */
export function isTokenExpired() {
    const user = getStoredUser();
    if (!user || !user.data || !user.data.token) return false;

    const token = user.data.token;

    if (isJwtFormat(token)) {
        const expSeconds = getJwtExp(token);
        if (expSeconds != null) {
            const expMs = expSeconds * 1000;
            return Date.now() >= expMs - EXPIRY_BUFFER_MS;
        }
    }

    const expiresAt = user.expiresAt;
    if (expiresAt != null && typeof expiresAt === 'number') {
        return Date.now() >= expiresAt;
    }

    return false;
}

/**
 * Clears session storage and disconnects socket. Does not redirect or show toast.
 */
export function logout() {
    localStorage.removeItem(USER_STORAGE_KEY);
    disconnectSocket();
}
